import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'antd';

/**
 * Reusable image lightbox modal with prev/next navigation, a photo counter,
 * keyboard support (←/→/Esc) and an optional details area per image.
 *
 * Props:
 *  - visible       : boolean
 *  - photos        : Array<{ url: string, name?: string, ...anything }>
 *  - initialIndex  : number (which photo to open on show; default 0)
 *  - onClose       : () => void
 *  - renderDetails : (photo, index) => ReactNode  (optional per-image details)
 *  - title         : base title label (default 'Photo')
 *  - width         : modal width (default 760)
 *  - zIndex        : modal z-index (default 1100)
 */
function ImagePreviewModal({
    visible = false,
    photos = [],
    initialIndex = 0,
    onClose,
    renderDetails = null,
    title = 'Photo',
    width = 760,
    zIndex = 1100,
}) {
    const [index, setIndex] = useState(initialIndex);

    // Re-seed the current photo whenever the modal is (re)opened at a new index.
    useEffect(() => {
        if (visible) setIndex(initialIndex);
    }, [visible, initialIndex]);

    const count = photos.length;
    const hasMultiple = count > 1;

    const showNext = () => setIndex((prev) => (count ? (prev + 1) % count : 0));
    const showPrev = () => setIndex((prev) => (count ? (prev - 1 + count) % count : 0));

    useEffect(() => {
        if (!visible) return undefined;
        const handler = (e) => {
            if (e.key === 'ArrowRight') showNext();
            else if (e.key === 'ArrowLeft') showPrev();
            else if (e.key === 'Escape' && onClose) onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [visible, count]);

    if (!visible || !count) return null;

    const safeIndex = Math.min(Math.max(index, 0), count - 1);
    const current = photos[safeIndex];
    if (!current || !current.url) return null;

    return (
        <Modal
            title={`${title} ${safeIndex + 1} of ${count}`}
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={width}
            zIndex={zIndex}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Button shape="circle" icon="left" onClick={showPrev} disabled={!hasMultiple} />
                <div style={{ flex: 1, textAlign: 'center', background: '#f0f2f5', borderRadius: 4, padding: 8 }}>
                    <img
                        src={current.url}
                        alt={current.name || ''}
                        style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
                    />
                </div>
                <Button shape="circle" icon="right" onClick={showNext} disabled={!hasMultiple} />
            </div>
            <div style={{ marginTop: 12 }}>
                <div style={{ marginBottom: 8, wordBreak: 'break-all' }}>
                    <a href={current.url} target="_blank" rel="noopener noreferrer">{current.name || 'Open original'}</a>
                </div>
                {renderDetails ? renderDetails(current, safeIndex) : null}
            </div>
        </Modal>
    );
}

export default ImagePreviewModal;
